/**
 * 审批单据 Word 导出 —— 导出布局（export_layout）共享工具
 * 布局模型（spec §3）：栅格行 rows/elements，每行元素 span 总和 ≤ 24
 * 校验规则与后端 ExportLayoutValidator 同规则（spec §3.2），保存时前端拦截并定位首个失效元素
 */

// 布局版本号
export const EXPORT_LAYOUT_VERSION = 1

// 单行栅格总数
export const GRID_SPAN_TOTAL = 24

// 颜色格式：#RRGGBB（STATIC/FIELD/DIVIDER 的 color 非空时必须匹配，对齐后端 COLOR_PATTERN）
export const COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/

// 元素类型（spec §3.1 共 4 种 + 设计器增强批新增 DIVIDER/SPACER/IMAGE，共 7 种）
export const ELEMENT_TYPE_STATIC = 'STATIC'
export const ELEMENT_TYPE_FIELD = 'FIELD'
export const ELEMENT_TYPE_DETAIL_TABLE = 'DETAIL_TABLE'
export const ELEMENT_TYPE_APPROVAL_TRAIL = 'APPROVAL_TRAIL'
export const ELEMENT_TYPE_DIVIDER = 'DIVIDER'
export const ELEMENT_TYPE_SPACER = 'SPACER'
export const ELEMENT_TYPE_IMAGE = 'IMAGE'

export const EXPORT_ELEMENT_TYPES = [
  ELEMENT_TYPE_STATIC,
  ELEMENT_TYPE_FIELD,
  ELEMENT_TYPE_DETAIL_TABLE,
  ELEMENT_TYPE_APPROVAL_TRAIL,
  ELEMENT_TYPE_DIVIDER,
  ELEMENT_TYPE_SPACER,
  ELEMENT_TYPE_IMAGE
]

// 块级元素：独占整行（span 强制 24，行内唯一元素）
export const BLOCK_ELEMENT_TYPES = [
  ELEMENT_TYPE_DETAIL_TABLE,
  ELEMENT_TYPE_APPROVAL_TRAIL,
  ELEMENT_TYPE_DIVIDER,
  ELEMENT_TYPE_SPACER,
  ELEMENT_TYPE_IMAGE
]

// 新元素属性默认值与范围（前后端同规则）
export const DIVIDER_DEFAULT_THICKNESS = 1
export const DIVIDER_DEFAULT_COLOR = '#999999'
export const DIVIDER_THICKNESS_MIN = 0.5
export const DIVIDER_THICKNESS_MAX = 10
export const SPACER_DEFAULT_HEIGHT = 12
export const SPACER_HEIGHT_MIN = 0
export const SPACER_HEIGHT_MAX = 200
export const IMAGE_DEFAULT_WIDTH = 60
export const IMAGE_WIDTH_MIN = 10
export const IMAGE_WIDTH_MAX = 160
// 行容器间距（磅）范围
export const ROW_SPACING_MIN = 0
export const ROW_SPACING_MAX = 72

/**
 * FIELD 是否显示字段名（showLabel 默认 true；存量布局无此属性视为 true）
 * 渲染语义为"标签：值"（后端 ExportFieldValueFormatter 落地）
 * @param {Object} el FIELD 元素
 * @returns {boolean}
 */
export function isFieldShowLabel(el) {
  return el?.showLabel !== false
}

// 系统字段白名单（spec §3.1）
export const SYSTEM_FIELDS = [
  { key: 'title', label: '审批标题' },
  { key: 'applicantName', label: '申请人' },
  { key: 'deptName', label: '申请部门' },
  { key: 'createTime', label: '提交时间' }
]

export const SYSTEM_FIELD_KEYS = SYSTEM_FIELDS.map(f => f.key)

// 元素 type 归一化（对齐后端 equalsIgnoreCase 语义）
function normalizeType(type) {
  return type == null ? '' : String(type).toUpperCase()
}

// 有效栅格宽度：span 缺省按 24 计（对齐后端 ExportLayout.Element.effectiveSpan）
export function effectiveSpan(el) {
  return el.span == null ? GRID_SPAN_TOTAL : (Number(el.span) || 0)
}

// 表单字段是否为 DETAIL 类型（对齐后端 equalsIgnoreCase 语义）
function isDetailField(field) {
  return String(field?.type ?? '').toUpperCase() === 'DETAIL'
}

/**
 * 收集 FIELD 占位可用的 fieldKey 全集：
 * 顶层非 DETAIL 字段 key ∪ summary.targetKey ∪ 系统字段白名单（spec §3.2）
 * @param {Array} fields 表单字段（getFieldList 结构：key/label/type/summary）
 * @returns {Set<string>}
 */
export function collectAvailableFieldKeys(fields) {
  const keys = new Set(SYSTEM_FIELD_KEYS)
  ;(fields || []).forEach(field => {
    if (!field) return
    if (isDetailField(field)) {
      (field.summary || []).forEach(sum => {
        if (sum?.targetKey) keys.add(sum.targetKey)
      })
      return
    }
    if (field.key) keys.add(field.key)
  })
  return keys
}

/**
 * 收集 DETAIL 类型字段 key 集合（DETAIL_TABLE.fieldKey 必须命中）
 * @param {Array} fields 表单字段
 * @returns {Set<string>}
 */
export function collectDetailFieldKeys(fields) {
  const keys = new Set()
  ;(fields || []).forEach(field => {
    if (isDetailField(field) && field.key) keys.add(field.key)
  })
  return keys
}

/**
 * 收集布局中所有失效（引用已删除/不存在字段）的 fieldKey，用于设计器标黄警示（spec §3.2 UX 决策 a）
 * @param {Object|null} layout 导出布局 { version, rows }
 * @param {Array} fields 表单字段
 * @returns {Set<string>}
 */
export function collectInvalidFieldKeys(layout, fields) {
  const invalid = new Set()
  if (!layout || !Array.isArray(layout.rows)) return invalid
  const availableKeys = collectAvailableFieldKeys(fields)
  const detailKeys = collectDetailFieldKeys(fields)
  layout.rows.forEach(row => {
    (row?.elements || []).forEach(el => {
      const type = normalizeType(el?.type)
      if (type === ELEMENT_TYPE_FIELD && !availableKeys.has(el.fieldKey)) {
        invalid.add(el.fieldKey)
      }
      if (type === ELEMENT_TYPE_DETAIL_TABLE && !detailKeys.has(el.fieldKey)) {
        invalid.add(el.fieldKey)
      }
    })
  })
  return invalid
}

/**
 * 校验导出布局（与后端 ExportLayoutValidator 同规则、同抛错顺序、同文案，spec §3.2）：
 * 每行先抛行间距（段前/段后 0-72 磅）；元素循环内抛元素级错误（未知类型、FIELD 缺 fieldKey、
 * FIELD fieldKey 失效、DETAIL_TABLE 引用非明细表、DIVIDER/SPACER/IMAGE 属性非空时超范围、
 * IMAGE 缺 fileKey，随后 STATIC/FIELD/DIVIDER 的 color 非空时必须为 #RRGGBB）；
 * 循环后再按 span 总和 → 独占行 → 块宽的顺序抛。
 * span 缺省按 24 计；块级元素独占一行且宽度必须为 24；type 比较忽略大小写。
 * 空布局（null/空 rows/全空行）直接放行（不启用导出）。
 * @param {Object|null} layout 导出布局 { version, rows }
 * @param {Array} fields 模板当前表单字段（getFieldList 结构）
 * @returns {string} 空串表示通过；否则为首个失效元素的错误提示（含 1-based 行号）
 */
export function validateExportLayout(layout, fields) {
  if (!layout || !Array.isArray(layout.rows)) return ''
  const availableKeys = collectAvailableFieldKeys(fields)
  const detailKeys = collectDetailFieldKeys(fields)
  for (let i = 0; i < layout.rows.length; i++) {
    const row = layout.rows[i]
    const elements = row?.elements || []
    // 空行不产出语义（导出时忽略），设计器输出前会剔除，校验层不加额外规则（与后端保持一致）
    if (elements.length === 0) continue
    const rowNo = i + 1
    // 行间距（磅）校验先于元素循环（对齐后端 validateRowSpacing 先行），段前/段后分开报错
    for (const [key, label] of [['spacingBefore', '段前'], ['spacingAfter', '段后']]) {
      const spacing = row[key]
      if (spacing == null) continue
      if (!(Number(spacing) >= ROW_SPACING_MIN && Number(spacing) <= ROW_SPACING_MAX)) {
        return `导出设计第 ${rowNo} 行${label}间距需在 ${ROW_SPACING_MIN}-${ROW_SPACING_MAX} 磅之间`
      }
    }
    let spanSum = 0
    let blockCount = 0
    let blockSpanViolation = false
    for (const el of elements) {
      if (!el) continue
      const type = normalizeType(el.type)
      const span = effectiveSpan(el)
      spanSum += span
      if (BLOCK_ELEMENT_TYPES.includes(type)) {
        blockCount++
        if (span !== GRID_SPAN_TOTAL) blockSpanViolation = true
      }
      // 元素级校验先于行级 span/独占校验抛错（与后端顺序一致）
      if (type === ELEMENT_TYPE_STATIC || type === ELEMENT_TYPE_APPROVAL_TRAIL) {
        // 静态文本 / 审批轨迹无 fieldKey 校验
      } else if (type === ELEMENT_TYPE_FIELD) {
        if (el.fieldKey == null || String(el.fieldKey).trim() === '') {
          return `导出设计第 ${rowNo} 行字段占位缺少 fieldKey`
        }
        // DETAIL 容器字段无标量语义，只能经 DETAIL_TABLE 块引用（与后端 fdb5e358 口径一致）
        if (detailKeys.has(el.fieldKey)) {
          return `导出设计第 ${rowNo} 行字段占位不支持明细表字段：${el.fieldKey}`
        }
        if (!availableKeys.has(el.fieldKey)) {
          return `导出设计第 ${rowNo} 行引用了已删除的字段：${el.fieldKey}`
        }
      } else if (type === ELEMENT_TYPE_DETAIL_TABLE) {
        if (!detailKeys.has(el.fieldKey)) {
          return `导出设计第 ${rowNo} 行明细表格引用的字段不是明细表：${el.fieldKey}`
        }
      } else if (type === ELEMENT_TYPE_DIVIDER) {
        // thickness 缺省不校验（对齐后端：仅非空时校验范围）
        if (el.thickness != null && !(Number(el.thickness) >= DIVIDER_THICKNESS_MIN && Number(el.thickness) <= DIVIDER_THICKNESS_MAX)) {
          return `导出设计第 ${rowNo} 行分隔线粗细需在 ${DIVIDER_THICKNESS_MIN}-${DIVIDER_THICKNESS_MAX} 磅之间`
        }
      } else if (type === ELEMENT_TYPE_SPACER) {
        if (el.height != null && !(Number(el.height) >= SPACER_HEIGHT_MIN && Number(el.height) <= SPACER_HEIGHT_MAX)) {
          return `导出设计第 ${rowNo} 行空白行高度需在 ${SPACER_HEIGHT_MIN}-${SPACER_HEIGHT_MAX} 磅之间`
        }
      } else if (type === ELEMENT_TYPE_IMAGE) {
        if (el.fileKey == null || String(el.fileKey).trim() === '') {
          return `导出设计第 ${rowNo} 行图片缺少 fileKey`
        }
        if (el.width != null && !(Number(el.width) >= IMAGE_WIDTH_MIN && Number(el.width) <= IMAGE_WIDTH_MAX)) {
          return `导出设计第 ${rowNo} 行图片宽度需在 ${IMAGE_WIDTH_MIN}-${IMAGE_WIDTH_MAX} 毫米之间`
        }
      } else {
        return `导出设计第 ${rowNo} 行存在不支持的元素类型：${el.type}`
      }
      // 颜色格式（#RRGGBB）校验在元素类型校验之后（对齐后端 validateElement 内顺序），非空才校验
      if ([ELEMENT_TYPE_STATIC, ELEMENT_TYPE_FIELD, ELEMENT_TYPE_DIVIDER].includes(type)) {
        const color = el.color
        if (color != null && String(color).trim() !== '' && !COLOR_PATTERN.test(String(color))) {
          return `导出设计第 ${rowNo} 行颜色格式不正确（应为 #RRGGBB）：${color}`
        }
      }
    }
    if (spanSum > GRID_SPAN_TOTAL) {
      return `导出设计第 ${rowNo} 行栅格总宽超出 ${GRID_SPAN_TOTAL}`
    }
    if (blockCount > 0 && elements.length > 1) {
      return `导出设计第 ${rowNo} 行的块级元素（明细表格/审批轨迹/分隔线/空白行/图片）必须独占一行`
    }
    if (blockSpanViolation) {
      return `导出设计第 ${rowNo} 行块级元素宽度必须为 ${GRID_SPAN_TOTAL}`
    }
  }
  return ''
}
